import { Effect, Scope, Stream } from 'effect'
import { ViewerPage } from './models'

export type DocumentRendererRenderMode = 'bitmap' | 'layoutOnly'

export type DocumentRendererSessionOpened<P extends ViewerPage = ViewerPage> = {
  sessionId: string
  pages: P[]
  renderMode?: DocumentRendererRenderMode
}

export type DocumentRendererPageRendered = {
  sessionId: string
  pageNumber: number
}

export type DocumentRendererPageTextExtracted = {
  sessionId: string
  pageNumber: number
  pageTextId: string
}

export type DocumentRendererEvent =
  | { type: 'pageRendered'; render: DocumentRendererPageRendered }
  | ({ type: 'pageTextExtracted' } & DocumentRendererPageTextExtracted)
  | { type: 'pageRenderFailed'; sessionId: string; pageNumber: number; error: string }

export class DocumentRendererOpenError extends Error {
  readonly _tag = 'DocumentRendererOpenError' as const

  constructor(readonly cause: Error) {
    super(cause.message)
  }
}

export type DocumentRendererService<Input = unknown, P extends ViewerPage = ViewerPage> = {
  open: (input: Input) => Effect.Effect<DocumentRendererSessionOpened<P>, DocumentRendererOpenError>
  close: (sessionId: string) => Effect.Effect<void>
  setDesiredPages: (sessionId: string, pages: number[]) => Effect.Effect<void>
  /**
   * Acquires this session's event queue in the caller's Scope before yielding
   * the Stream. Once this Effect succeeds, later renderer events cannot race
   * ahead of subscription activation; closing the Scope releases the queue.
   */
  events: (
    sessionId: string
  ) => Effect.Effect<Stream.Stream<DocumentRendererEvent>, never, Scope.Scope>
  attachPageSurface: (
    sessionId: string,
    pageNumber: number,
    surfaceId: string
  ) => Effect.Effect<void>
  detachPageSurface: (
    sessionId: string,
    pageNumber: number,
    surfaceId: string
  ) => Effect.Effect<void>
  releasePageText: (pageTextId: string) => Effect.Effect<void>
}
